// src/components/institution/sections/ResourcesSection.jsx
import React, { useState, useEffect } from 'react';
import { 
  FileText, Download, Eye, Folder, Search, Plus, Filter, Image as ImageIcon,
  Package, BookOpen, Settings, Monitor, Printer, Table,
  Video, Link, Upload, Edit, Trash2, ExternalLink, Camera,
  Calendar, Users, Shield, Briefcase, Grid, List, Paperclip,
  AlertTriangle, CheckCircle, Clock, MapPin
} from 'lucide-react';
import { institutionService } from '../../../services/institutionService';

// Resource type configurations
const RESOURCE_TYPES = {
  physical: {
    label: 'Physical Resources',
    icon: Package,
    color: 'blue',
    description: 'Institution\'s physical assets and equipment',
    categories: [
      { value: 'technology', label: 'Technology & Equipment', icon: Monitor },
      { value: 'furniture', label: 'Furniture & Fixtures', icon: Table },
      { value: 'vehicles', label: 'Vehicles & Transport', icon: Package },
      { value: 'facility', label: 'Facility Infrastructure', icon: Package },
      { value: 'books', label: 'Physical Books & Library', icon: BookOpen },
      { value: 'supplies', label: 'Office & Teaching Supplies', icon: Package },
      { value: 'safety', label: 'Safety & Security Equipment', icon: Shield },
      { value: 'sports', label: 'Sports & Recreation', icon: Package }
    ]
  },
  learning: {
    label: 'Learning & Teaching Materials',
    icon: BookOpen,
    color: 'green',
    description: 'Educational content and teaching resources',
    categories: [
      { value: 'curriculum', label: 'Curriculum Materials', icon: BookOpen },
      { value: 'multimedia', label: 'Video & Audio Content', icon: Video },
      { value: 'documents', label: 'Documents & PDFs', icon: FileText },
      { value: 'interactive', label: 'Interactive Content', icon: Package },
      { value: 'assessments', label: 'Assessments & Quizzes', icon: FileText },
      { value: 'presentations', label: 'Presentations & Slides', icon: FileText },
      { value: 'external', label: 'External Links & Resources', icon: Link },
      { value: 'templates', label: 'Teaching Templates', icon: FileText }
    ]
  },
  management: {
    label: 'Management Resources',
    icon: Briefcase,
    color: 'purple',
    description: 'Administrative and governance materials',
    categories: [
      { value: 'policies', label: 'Policies & Procedures', icon: FileText },
      { value: 'plans', label: 'Strategic & Annual Plans', icon: Calendar },
      { value: 'reports', label: 'Reports & Analytics', icon: FileText },
      { value: 'legal', label: 'Legal & Compliance', icon: Shield },
      { value: 'hr', label: 'Human Resources', icon: Users },
      { value: 'finance', label: 'Financial Documents', icon: Package },
      { value: 'governance', label: 'Governance & Board', icon: Briefcase },
      { value: 'quality', label: 'Quality Assurance', icon: CheckCircle }
    ]
  }
};

const STATUS_CONFIG = {
  available: { label: 'Available', color: 'text-green-600 bg-green-100', icon: CheckCircle },
  inUse: { label: 'In Use', color: 'text-blue-600 bg-blue-100', icon: Clock },
  maintenance: { label: 'Maintenance', color: 'text-yellow-600 bg-yellow-100', icon: AlertTriangle },
  damaged: { label: 'Damaged', color: 'text-red-600 bg-red-100', icon: AlertTriangle },
  retired: { label: 'Retired', color: 'text-gray-600 bg-gray-100', icon: Package }
};

export const ResourcesSection = ({ 
  bookId, 
  data, 
  settings, 
  styling, 
  onResourceClick, 
  isAdmin 
}) => {
  const [resources, setResources] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    loadResources();
  }, [bookId]);

  useEffect(() => {
    filterResources();
  }, [resources, selectedType, selectedCategory, searchQuery]);

  const loadResources = async () => {
    try {
      const resourcesData = await institutionService.getResources(bookId);
      const publicResources = isAdmin ? resourcesData : resourcesData.filter(r => r.accessLevel === 'public');
      setResources(publicResources);
    } catch (error) {
      console.error('Error loading resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterResources = () => {
    let filtered = resources;

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(r => r.resourceType === selectedType);
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(r => r.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.title?.toLowerCase().includes(query) ||
        r.description?.toLowerCase().includes(query) ||
        r.tags?.some(tag => tag.toLowerCase().includes(query)) ||
        r.assetId?.toLowerCase().includes(query)
      );
    }

    setFilteredResources(filtered);
  };

  const getResourceIcon = (resource) => {
    const typeConfig = RESOURCE_TYPES[resource.resourceType];
    if (!typeConfig) return FileText;

    const categoryConfig = typeConfig.categories.find(c => c.value === resource.category);
    return categoryConfig?.icon || typeConfig.icon;
  };

  const getResourceTypeColor = (type) => {
    const colors = {
      physical: 'border-blue-200 bg-blue-50',
      learning: 'border-green-200 bg-green-50',
      management: 'border-purple-200 bg-purple-50'
    };
    return colors[type] || 'border-gray-200 bg-gray-50';
  };

  const renderResourceCard = (resource) => {
    const IconComponent = getResourceIcon(resource);
    const typeConfig = RESOURCE_TYPES[resource.resourceType];
    const statusConfig = resource.status ? STATUS_CONFIG[resource.status] : null;
    const hasImages = resource.images && resource.images.length > 0;
    const hasDocuments = resource.documents && resource.documents.length > 0;

    return (
      <div 
        key={resource.id}
        className={`bg-white rounded-xl border-2 p-6 hover:shadow-lg transition-all cursor-pointer ${getResourceTypeColor(resource.resourceType)}`}
        onClick={() => onResourceClick && onResourceClick(resource)}
      >
        {/* Image Preview Section */}
        {hasImages && (
          <div className="relative mb-4">
            <img
              src={resource.images[0].url}
              alt={resource.title}
              className="w-full h-48 object-cover rounded-lg"
            />
            {resource.images.length > 1 && (
              <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded-full text-sm flex items-center gap-1">
                <Camera className="h-4 w-4" />
                {resource.images.length}
              </div>
            )}
            <div className="absolute top-2 left-2">
              <div className={`p-2 rounded-lg ${
                typeConfig?.color === 'blue' ? 'bg-blue-100 text-blue-600' : 
                typeConfig?.color === 'green' ? 'bg-green-100 text-green-600' : 
                'bg-purple-100 text-purple-600'
              } bg-opacity-90 backdrop-blur-sm`}>
                <IconComponent className="h-5 w-5" />
              </div>
            </div>
          </div>
        )}

        {/* Header (when no images) */}
        {!hasImages && (
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className={`p-2 rounded-lg ${
                typeConfig?.color === 'blue' ? 'bg-blue-100 text-blue-600' : 
                typeConfig?.color === 'green' ? 'bg-green-100 text-green-600' : 
                'bg-purple-100 text-purple-600'
              }`}>
                <IconComponent className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 truncate text-lg">
                  {resource.title}
                </h3>
                <p className="text-sm text-gray-500 capitalize">
                  {typeConfig?.label} • {resource.category?.replace(/([A-Z])/g, ' $1')}
                </p>
              </div>
            </div>
            
            {/* Status Badge for Physical Resources */}
            {resource.resourceType === 'physical' && statusConfig && (
              <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusConfig.color}`}>
                <statusConfig.icon className="h-3 w-3" />
                {statusConfig.label}
              </div>
            )}
          </div>
        )}

        {/* Title and Category (when images exist) */}
        {hasImages && (
          <div className="mb-4">
            <h3 className="font-semibold text-gray-900 text-lg mb-1">
              {resource.title}
            </h3>
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500 capitalize">
                {typeConfig?.label} • {resource.category?.replace(/([A-Z])/g, ' $1')}
              </p>
              {resource.resourceType === 'physical' && statusConfig && (
                <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusConfig.color}`}>
                  <statusConfig.icon className="h-3 w-3" />
                  {statusConfig.label}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Description */}
        {resource.description && (
          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
            {resource.description}
          </p>
        )}

        {/* Resource Details */}
        <div className="space-y-2 mb-4">
          {/* Asset ID for Physical Resources */}
          {resource.resourceType === 'physical' && resource.assetId && (
            <div className="flex items-center gap-2 text-sm">
              <Package className="h-4 w-4 text-gray-400" />
              <span className="text-gray-600">Asset ID: {resource.assetId}</span>
            </div>
          )}

          {/* Location for Physical Resources */}
          {resource.resourceType === 'physical' && resource.location && (
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="h-4 w-4 text-gray-400" />
              <span className="text-gray-600">{resource.location}</span>
            </div>
          )}

          {/* File attachments summary */}
          <div className="flex items-center gap-4 text-sm">
            {hasImages && (
              <div className="flex items-center gap-1 text-blue-600">
                <Camera className="h-4 w-4" />
                <span>{resource.images.length} image{resource.images.length !== 1 ? 's' : ''}</span>
              </div>
            )}
            
            {hasDocuments && (
              <div className="flex items-center gap-1 text-green-600">
                <Paperclip className="h-4 w-4" />
                <span>{resource.documents.length} document{resource.documents.length !== 1 ? 's' : ''}</span>
              </div>
            )}

            {resource.fileSize && (
              <div className="flex items-center gap-1 text-gray-600">
                <FileText className="h-4 w-4" />
                <span>{formatFileSize(resource.fileSize)}</span>
              </div>
            )}
          </div>

          {/* Tags */}
          {resource.tags && resource.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {resource.tags.slice(0, 3).map((tag, index) => (
                <span 
                  key={index}
                  className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md"
                >
                  {tag}
                </span>
              ))}
              {resource.tags.length > 3 && (
                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">
                  +{resource.tags.length - 3}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Updated {new Date(resource.updatedAt?.toDate() || resource.updatedAt).toLocaleDateString()}</span>
          </div>
          
          <div className="flex gap-2">
            {/* View Images */}
            {hasImages && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(resource.images[0].url, '_blank');
                }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-blue-600"
                title="View Images"
              >
                <ImageIcon className="h-4 w-4" />
              </button>
            )}

            {/* View/Download Actions */}
            {resource.fileUrl && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(resource.fileUrl, '_blank');
                }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                style={{ color: styling?.primaryColor }}
                title="View Resource"
              >
                <Eye className="h-4 w-4" />
              </button>
            )}
            
            {resource.downloadUrl && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(resource.downloadUrl, '_blank');
                }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                style={{ color: styling?.primaryColor }}
                title="Download"
              >
                <Download className="h-4 w-4" />
              </button>
            )}

            {resource.externalUrl && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(resource.externalUrl, '_blank');
                }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                style={{ color: styling?.primaryColor }}
                title="Open Link"
              >
                <ExternalLink className="h-4 w-4" />
              </button>
            )}

            {/* Documents indicator */}
            {hasDocuments && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Could open a documents viewer or modal
                }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-green-600"
                title={`${resource.documents.length} supporting documents`}
              >
                <Paperclip className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderListView = (resource) => {
    const IconComponent = getResourceIcon(resource);
    const typeConfig = RESOURCE_TYPES[resource.resourceType];
    const statusConfig = resource.status ? STATUS_CONFIG[resource.status] : null;

    return (
      <div 
        key={resource.id}
        className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all cursor-pointer"
        onClick={() => onResourceClick && onResourceClick(resource)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`p-2 rounded-lg ${typeConfig?.color === 'blue' ? 'bg-blue-100 text-blue-600' : 
              typeConfig?.color === 'green' ? 'bg-green-100 text-green-600' : 
              'bg-purple-100 text-purple-600'}`}>
              <IconComponent className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900 truncate">
                {resource.title}
              </h3>
              <p className="text-sm text-gray-500">
                {typeConfig?.label} • {resource.category}
                {resource.assetId && ` • ID: ${resource.assetId}`}
                {resource.location && ` • ${resource.location}`}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Status for Physical Resources */}
            {resource.resourceType === 'physical' && statusConfig && (
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                {statusConfig.label}
              </div>
            )}
            
            {/* Actions */}
            <div className="flex gap-1">
              {resource.url && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(resource.url, '_blank');
                  }}
                  className="p-1 rounded hover:bg-gray-100 text-gray-600"
                >
                  <Eye className="h-4 w-4" />
                </button>
              )}
              {resource.downloadUrl && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(resource.downloadUrl, '_blank');
                  }}
                  className="p-1 rounded hover:bg-gray-100 text-gray-600"
                >
                  <Download className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i))} ${sizes[i]}`;
  };

  const handleEditResource = (resource) => {
    // Implementation for editing resource
    console.log('Edit resource:', resource);
  };

  const handleDeleteResource = async (resourceId) => {
    if (window.confirm('Are you sure you want to delete this resource?')) {
      try {
        await institutionService.deleteResource(bookId, resourceId);
        await loadResources();
      } catch (error) {
        console.error('Error deleting resource:', error);
      }
    }
  };

  // Get available categories based on selected type
  const availableCategories = selectedType === 'all' 
    ? []
    : RESOURCE_TYPES[selectedType]?.categories || [];

  if (loading) {
    return (
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: styling?.primaryColor || '#1e40af' }}
          >
            Institution Resources
          </h2>
          <div className="w-24 h-1 mx-auto mb-8" style={{ backgroundColor: styling?.primaryColor }} />
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Access physical assets, learning materials, and management resources.
          </p>
        </div>

        {/* Controls */}
        <div className="mb-8 space-y-4">
          {/* Search and View Toggle */}
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="flex flex-1 max-w-md">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search resources..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'grid' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'list' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>

              {/* Add Resource Button (Admin Only) */}
              {isAdmin && (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Add Resource
                </button>
              )}
            </div>
          </div>

          {/* Type Filter */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedType('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedType === 'all'
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Resources
            </button>
            {Object.entries(RESOURCE_TYPES).map(([type, config]) => {
              const IconComponent = config.icon;
              return (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                    selectedType === type
                      ? 'text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  style={{ 
                    backgroundColor: selectedType === type ? styling?.primaryColor || '#1e40af' : undefined
                  }}
                >
                  <IconComponent className="h-4 w-4" />
                  {config.label}
                </button>
              );
            })}
          </div>

          {/* Category Filter */}
          {availableCategories.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  selectedCategory === 'all'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All Categories
              </button>
              {availableCategories.map((category) => (
                <button
                  key={category.value}
                  onClick={() => setSelectedCategory(category.value)}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    selectedCategory === category.value
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Results */}
        {filteredResources.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No Resources Found</h3>
            <p className="text-gray-500">
              {searchQuery 
                ? `No resources match "${searchQuery}"`
                : 'No resources available in this category.'
              }
            </p>
          </div>
        ) : (
          <>
            {/* Results Summary */}
            <div className="mb-6 text-sm text-gray-600">
              Showing {filteredResources.length} of {resources.length} resources
            </div>

            {/* Resource Grid/List */}
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredResources.map(renderResourceCard)}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredResources.map(renderListView)}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};